import { GUARD_COPY } from "../domain/bookingState";
import { SYSTEM_ERROR } from "@/modules/shared/forms";
import type { Failure } from "@/modules/shared/failure";

/**
 * The **web's** copy for every code a Business booking command can refuse with
 * (R22 T3).
 *
 * A separate file from `./business.ts` for one reason worth knowing: that file
 * is `"use server"`, where every export must be an async function, so a pure
 * lookup cannot live there and therefore could not be unit-tested there either.
 * Here it is an ordinary module, and `./businessCopy.test.ts` holds it to the
 * one promise that matters, that no code a core can return falls through to the
 * generic apology.
 *
 * **Every sentence below is unchanged from before the cores existed.** T3 moved
 * where a sentence is chosen, never which sentence it is. The clinic-facing
 * wording differs from the Consumer's for the same code on purpose: a Manager
 * is deciding what to do next, and "That slot is full." is what they need,
 * while a Consumer is told "This time is already booked. Please pick another."
 */
export function businessCopyFor(failure: Failure): string {
  switch (failure.code) {
    case "not_found":
      return "This booking could not be found.";
    // H16: the hour left the practitioner's derived calendar after the request.
    case "block_unavailable":
      return "This time is no longer in your schedule.";
    // The loser of the approval race (core model §3, test #1).
    case "slot_taken":
      return "That slot is full.";
    // H38: the person is already booked somewhere else in this hour. The copy
    // says nothing about where, because which other clinic they work at is that
    // clinic's business and none of this one's.
    case "practitioner_held_elsewhere":
      return "This practitioner is not available at this time. Pick another time, or assign somebody else.";
    case "practitioner_required":
      return "Choose a practitioner to assign this booking to."; // H23
    case "practitioner_unavailable":
      return "That practitioner isn't available for this time. Pick another."; // H23
    case "location_unavailable":
      return "That practitioner doesn't offer the chosen service location."; // H24
    case "home_address_required":
      return "Add the customer's address before confirming an at-home visit."; // H24
    case "target_block_unavailable":
      return "That time is not in this practitioner's schedule."; // H49
    case "past_start":
      return "You can only move a booking to a future time.";
    case "same_block":
      return "That is already the booking's time.";
    case "malformed_input":
      return "That request was malformed. Please try again.";
    case "booking_not_open":
    case "booking_expired":
    case "booking_not_active":
    case "booking_already_complete":
    case "cancellation_window":
    case "reschedule_window":
    case "no_pending_suggestion":
    case "suggestion_expired":
      return GUARD_COPY[failure.code];
    default:
      return SYSTEM_ERROR;
  }
}
